import { useMemo, useState } from 'react';

import {
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { Box, Chip, Collapse, IconButton, Link, Paper, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { severityColor } from '@/lib/alert-severity';
import { type ActiveAlert, getActiveAlerts } from '@/lib/api/alerts';
import { useAlertBannerStore } from '@/store/alertBannerStore';

const PREVIEW_COUNT = 3;

/**
 * 심각도 점 — 목록 경보 배지와 같은 시각 언어.
 *
 * ⚠️ className을 반드시 그대로 넘겨야 한다. Chip의 icon 슬롯에 쓰면 MUI가
 *    cloneElement로 `MuiChip-icon` 클래스를 주입하는데, 여기서 삼켜버리면
 *    클래스가 DOM에 붙지 않아 아이콘 여백 스타일이 통째로 무시된다.
 */
function Dot({ color, size = 8, className }: { color: string; size?: number; className?: string }) {
  return (
    <Box
      component="span"
      className={className}
      sx={{ width: size, height: size, borderRadius: '50%', bgcolor: color, flexShrink: 0 }}
    />
  );
}

/**
 * 의뢰서 P3-2 경보 배너.
 *
 * 표현 원칙 — 색은 점에만 쓰고 배경은 채우지 않는다.
 *   시설 대부분이 경보 대상인 상황이 흔해(운영 데이터 기준 30명 중 28명) 배너가
 *   상시 떠 있게 된다. 전체 폭 빨간 배경을 계속 띄우면 눈이 금세 무뎌져서,
 *   정작 급한 경보가 왔을 때 구분되지 않는다. 왼쪽 4px 강조선으로 존재감만 남긴다.
 *
 * 동작은 종전과 같다:
 * - 활성 경보가 있을 때만 노출, 화면 상단 sticky
 * - 처음 PREVIEW_COUNT(3)건만 요약 + 펼치기로 전체 확인
 * - dismiss는 세션 한정 (alertBannerStore — persist 미적용)
 * - 클릭 시 해당 입소자 상세 드로어 오픈
 * - 30초 polling — 자정 cron 산출 후 5분 내 직원 화면 반영
 */
export function AlertBanner() {
  const navigate = useNavigate();
  const dismissed = useAlertBannerStore((s) => s.dismissed);
  const dismiss = useAlertBannerStore((s) => s.dismiss);
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['alerts', 'active'],
    queryFn: () => getActiveAlerts(),
    refetchInterval: 30 * 1000,
    staleTime: 10 * 1000,
  });

  const alerts = useMemo<ActiveAlert[]>(() => data ?? [], [data]);
  const critical = useMemo(() => alerts.filter((a) => a.severity === 'CRITICAL'), [alerts]);
  const warning = useMemo(() => alerts.filter((a) => a.severity === 'WARNING'), [alerts]);

  if (dismissed || isLoading || alerts.length === 0) return null;

  const preview = expanded ? alerts : alerts.slice(0, PREVIEW_COUNT);
  const hidden = Math.max(0, alerts.length - PREVIEW_COUNT);
  const accent = severityColor(critical.length > 0 ? 'CRITICAL' : 'WARNING').dot;

  const handleClick = (a: ActiveAlert) => {
    navigate(`/residents?detail=${a.residentId}&tab=today`);
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 64,
        zIndex: (theme) => theme.zIndex.appBar - 1,
        mb: 2,
      }}
    >
      <Paper
        variant="outlined"
        data-testid="alert-banner"
        sx={{
          // 배경은 흰색 그대로. 왼쪽 강조선으로만 심각도를 드러낸다.
          borderLeft: `4px solid ${accent}`,
          px: 2,
          py: 1,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
            {critical.length > 0 && (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Dot color={severityColor('CRITICAL').dot} />
                <Typography variant="body2" fontWeight={700}>
                  긴급 {critical.length}
                </Typography>
              </Stack>
            )}
            {warning.length > 0 && (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Dot color={severityColor('WARNING').dot} />
                <Typography variant="body2" fontWeight={700}>
                  일반 {warning.length}
                </Typography>
              </Stack>
            )}
          </Stack>

          {/* 건수 묶음과 미리보기 칩이 붙어 보이지 않도록 경계에 여백을 둔다
              (Stack spacing 12px + 여기 4px = 16px) */}
          <Box sx={{ flex: 1, minWidth: 0, pl: 0.5 }}>
            <Collapse in={expanded} collapsedSize={32} timeout={150}>
              <Stack
                direction="row"
                spacing={1}
                flexWrap={expanded ? 'wrap' : 'nowrap'}
                useFlexGap
                alignItems="center"
                // Collapse가 접힘 상태에서 32px 박스를 만드는데 이 Stack은 칩 높이(~24px)
                // 만큼만 차지해 위로 붙는다. alignItems는 Stack 자기 높이 안에서만
                // 동작하므로, 박스 높이를 맞춰줘야 칩이 세로 가운데에 놓인다.
                sx={{ minHeight: 32 }}
              >
                {preview.map((a) => {
                  const room = a.roomNumber ? `${a.roomNumber}호` : '미배정';
                  const idLabel = a.patientCode ?? a.name ?? '미식별';
                  return (
                    <Chip
                      key={a.id}
                      size="small"
                      variant="outlined"
                      icon={<Dot color={severityColor(a.severity).dot} size={7} />}
                      label={`${room} · ${idLabel} · ${a.message}`}
                      clickable
                      onClick={() => handleClick(a)}
                      sx={{
                        maxWidth: 320,
                        borderColor: 'divider',
                        // 칩이 알약 모양이라 왼쪽 테두리가 둥글게 깎여, 7px 점에는
                        // MUI 기본 여백(4px)이 부족하다. 점↔글자 간격은 라벨 패딩에 맡긴다.
                        '& .MuiChip-icon': { ml: 1, mr: 0 },
                      }}
                    />
                  );
                })}
                {!expanded && hidden > 0 && (
                  <Link
                    component="button"
                    underline="hover"
                    onClick={() => setExpanded(true)}
                    sx={{ whiteSpace: 'nowrap', fontSize: '0.8125rem', fontWeight: 600 }}
                  >
                    +{hidden}건 더 보기
                  </Link>
                )}
              </Stack>
            </Collapse>
          </Box>

          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            {alerts.length > PREVIEW_COUNT && (
              <IconButton
                size="small"
                onClick={() => setExpanded((v) => !v)}
                aria-label={expanded ? '접기' : '모두 보기'}
              >
                {expanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            )}
            <IconButton size="small" onClick={dismiss} aria-label="배너 닫기">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
